import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMobileAuth } from '../../context/MobileAuthContext';
import { formatTime as formatTimeUtil } from '../../utils/dateFormatting';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  ChevronLeft,
  ChevronRight,
  Calendar,
  Loader2,
  MapPin,
  Truck,
  Search,
  Plus,
  Filter,
  X,
  Folder,
  List,
  Clock,
} from 'lucide-react';
import { format, addDays, subDays } from 'date-fns';
import MobileLayout from '../../components/mobile/MobileLayout';
import ScheduleFolderView from '../../components/schedule/ScheduleFolderView';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const MobileSchedule = () => {
  const navigate = useNavigate();
  const { api, operator, tenantSettings, isTimesheetOnly } = useMobileAuth();
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  const [jobDayDetails, setJobDayDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState('list');
  const [foldersEnabled, setFoldersEnabled] = useState(false);
  const [quickReserveEnabled, setQuickReserveEnabled] = useState(false);
  const timeFormat = tenantSettings?.time_format || '24h';

  useEffect(() => {
    api.get('/api/feature-flags/resolved')
      .then(res => {
        const flags = res.data?.flags || res.data || {};
        setFoldersEnabled(!!flags.schedule_folders);
        setQuickReserveEnabled(!!flags.quick_reserve);
      })
      .catch(() => { setFoldersEnabled(false); setQuickReserveEnabled(false); });
  }, [api]);

  useEffect(() => {
    if (isTimesheetOnly()) navigate('/mobile/dashboard', { replace: true });
  }, [isTimesheetOnly, navigate]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [jobsRes, customersRes, sitesRes, dayDetailsRes] = await Promise.all([
        api.get('/api/jobs'),
        api.get('/api/customers'),
        api.get('/api/sites'),
        api.get('/api/job-day-details')
      ]);
      setJobs(jobsRes.data || []);
      setCustomers(customersRes.data || []);
      setSites(sitesRes.data || []);
      const detailsByJob = {};
      (dayDetailsRes.data || []).forEach(d => {
        if (!detailsByJob[d.job_id]) detailsByJob[d.job_id] = {};
        detailsByJob[d.job_id][d.date] = d;
      });
      setJobDayDetails(detailsByJob);
    } catch (error) {
      console.error('Failed to fetch schedule data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer?.company_name || 'Unknown';
  };

  const getSiteName = (siteId) => {
    const site = sites.find(s => s.id === siteId);
    return site?.name || site?.street_address || '';
  };

  const getDateLabel = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selected = new Date(selectedDate);
    selected.setHours(0, 0, 0, 0);
    const diffDays = Math.round((selected - today) / (1000 * 60 * 60 * 24));
    if (diffDays === -1) return 'Yesterday';
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    return format(selectedDate, 'EEE, d MMM');
  };

  const isToday = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selected = new Date(selectedDate);
    selected.setHours(0, 0, 0, 0);
    return today.getTime() === selected.getTime();
  };

  const navigateDate = (direction) => {
    setSelectedDate(direction === 1 ? addDays(selectedDate, 1) : subDays(selectedDate, 1));
  };

  const getFilteredJobs = () => {
    const today = format(new Date(), 'yyyy-MM-dd');
    const selectedDateStr = format(selectedDate, 'yyyy-MM-dd');

    return jobs.filter(job => {
      const matchesSearch = searchTerm === '' ||
        job.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        getCustomerName(job.customer_id).toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(job.job_number || '').includes(searchTerm);

      if (!matchesSearch) return false;

      switch (statusFilter) {
        case 'today':
          return today >= job.start_date && today <= (job.end_date || job.start_date);
        case 'unallocated':
          return job.status === 'not_allocated';
        case 'allocated':
          return job.status === 'allocated' || job.status === 'sent';
        case 'confirmed':
          return job.status === 'confirmed';
        case 'active':
          return ['not_allocated', 'allocated', 'sent', 'confirmed'].includes(job.status);
        case 'all':
        default:
          return selectedDateStr >= job.start_date && selectedDateStr <= (job.end_date || job.start_date);
      }
    });
  };

  // Resolve the effective start/end time for a job on the currently selected date,
  // honouring per-day custom overrides for multi-day jobs.
  const getJobDayTimes = (job) => {
    const selectedDateStr = format(selectedDate, 'yyyy-MM-dd');
    const override = jobDayDetails[job.id]?.[selectedDateStr];
    return {
      start_time: override?.start_time || job.start_time,
      end_time: override?.end_time || job.end_time,
      overridden: !!(override?.start_time || override?.end_time),
    };
  };

  const sortedJobs = [...getFilteredJobs()].sort((a, b) => {
    if (a.status === 'not_allocated' && b.status !== 'not_allocated') return -1;
    if (b.status === 'not_allocated' && a.status !== 'not_allocated') return 1;
    return (getJobDayTimes(a).start_time || '99:99').localeCompare(getJobDayTimes(b).start_time || '99:99');
  });

  const formatTime = (timeStr) => {
    if (!timeStr) return 'TBD';
    return formatTimeUtil(timeStr, timeFormat);
  };

  return (
    <MobileLayout>
      {/* Header */}
      <div className="bg-[var(--m-bg-card)] px-4 py-4 sticky top-0 z-10 safe-top">
        <div className="flex items-center justify-between mb-3">
          <h1 className="text-[var(--m-text)] font-bold text-lg" data-testid="schedule-title">Schedule</h1>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className={`p-2 ${showFilters ? 'text-[#ffd108]' : 'text-[var(--m-text-secondary)]'}`}
              data-testid="schedule-filter-btn"
            >
              <Filter className="h-5 w-5" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/mobile/schedule/new')}
              className="text-[#ffd108] p-2"
              data-testid="schedule-add-job-btn"
            >
              <Plus className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--m-text-muted)]" />
          <Input
            placeholder="Search jobs, customers, job #..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 bg-[var(--m-bg)] border-[var(--m-border)] text-[var(--m-text)] placeholder:text-[var(--m-text-muted)]"
            data-testid="schedule-search-input"
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--m-text-muted)]">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="mt-3 p-3 bg-[var(--m-bg)] rounded-lg space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-[var(--m-text-secondary)] text-sm">Status Filter</span>
              {statusFilter !== 'all' && (
                <button onClick={() => setStatusFilter('all')} className="text-[#ffd108] text-xs">Clear</button>
              )}
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="bg-[var(--m-bg-card)] border-[var(--m-border)] text-[var(--m-text)]" data-testid="schedule-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[var(--m-bg-card)] border-[var(--m-border)] text-[var(--m-text)]">
                <SelectItem value="all">All (by date)</SelectItem>
                <SelectItem value="today">Today's Jobs</SelectItem>
                <SelectItem value="unallocated">Not Allocated</SelectItem>
                <SelectItem value="allocated">Allocated / Sent</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="active">All Active</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        {/* View toggle (Northlands beta) */}
        {foldersEnabled && (
          <div className="mt-3 flex rounded-lg bg-[var(--m-bg)] p-1" data-testid="mobile-schedule-view-toggle">
            <button
              onClick={() => setViewMode('list')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === 'list' ? 'bg-[#ffd108] text-black' : 'text-[var(--m-text-secondary)]'}`}
              data-testid="mobile-view-list-btn"
            >
              <List className="h-4 w-4" /> List
            </button>
            <button
              onClick={() => setViewMode('folders')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === 'folders' ? 'bg-[#ffd108] text-black' : 'text-[var(--m-text-secondary)]'}`}
              data-testid="mobile-view-folders-btn"
            >
              <Folder className="h-4 w-4" /> Folders
            </button>
          </div>
        )}
      </div>

      {/* Date Navigator */}
      {(statusFilter === 'all' || viewMode === 'folders') && (
        <div className="px-4 py-3 bg-[var(--m-bg-card)]">
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="sm" onClick={() => navigateDate(-1)} className="text-[var(--m-text-secondary)]">
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-[#ffd108]" />
              <span className="text-[var(--m-text)] font-medium">{getDateLabel()}</span>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigateDate(1)} className="text-[var(--m-text-secondary)]">
              <ChevronRight className="h-5 w-5" />
            </Button>
          </div>
          {!isToday() && (
            <div className="flex justify-center mt-2">
              <Button variant="ghost" size="sm" onClick={() => setSelectedDate(new Date())} className="text-[#ffd108] text-xs h-7 px-3">
                <Calendar className="h-3 w-3 mr-1" />
                Today
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Folders View (Northlands beta) */}
      {viewMode === 'folders' && (
        <div className="p-4" data-testid="mobile-schedule-folders-view">
          <ScheduleFolderView
            api={api}
            basePath="/api"
            date={selectedDate}
            showConfigLink={false}
            quickReserve={quickReserveEnabled}
            onConverted={(jobId) => navigate(`/mobile/schedule/${jobId}`)}
            onJobClick={(jobId) => navigate(`/mobile/schedule/${jobId}`)}
          />
        </div>
      )}

      {/* Status filter label */}
      {viewMode === 'list' && statusFilter !== 'all' && (
        <div className="px-4 py-2 bg-[var(--m-bg-card)] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-[#ffd108]" />
            <span className="text-[var(--m-text)] text-sm">
              {statusFilter === 'today' && "Today's Jobs"}
              {statusFilter === 'unallocated' && 'Not Allocated'}
              {statusFilter === 'allocated' && 'Allocated / Sent'}
              {statusFilter === 'confirmed' && 'Confirmed'}
              {statusFilter === 'active' && 'All Active'}
            </span>
            <Badge className="bg-[#ffd108]/20 text-[#ffd108] text-xs">{sortedJobs.length}</Badge>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setStatusFilter('all')} className="text-[var(--m-text-secondary)] p-1">
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Jobs List */}
      {viewMode === 'list' && (
      <div className="p-4">
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-[#ffd108]" />
          </div>
        ) : sortedJobs.length === 0 ? (
          <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
            <CardContent className="py-8 text-center">
              <Calendar className="h-12 w-12 mx-auto text-gray-600 mb-3" />
              <p className="text-[var(--m-text-secondary)]">
                {searchTerm ? 'No matching jobs found' : 'No jobs for this date'}
              </p>
              <Button className="mt-4 btn-volt" onClick={() => navigate('/mobile/schedule/new')} data-testid="schedule-empty-create-btn">
                <Plus className="h-4 w-4 mr-2" />
                Create Job
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {sortedJobs.map((job) => {
              const isMultiDay = job.end_date && job.end_date !== job.start_date;
              const dayTimes = getJobDayTimes(job);
              return (
                <Card
                  key={job.id}
                  className="bg-[var(--m-bg-card)] border-[var(--m-border)] cursor-pointer hover:border-[#ffd108] transition-colors"
                  onClick={() => navigate(`/mobile/schedule/${job.id}`)}
                  data-testid={`schedule-job-card-${job.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center flex-wrap gap-2 mb-2">
                          {job.job_number && (
                            <span className="text-[var(--m-text-secondary)] text-xs font-mono">#{job.job_number}</span>
                          )}
                          <span className="text-[#ffd108] font-bold" data-testid={`schedule-job-time-${job.id}`}>
                            {formatTime(dayTimes.start_time)}{dayTimes.end_time ? ` – ${formatTime(dayTimes.end_time)}` : ''}
                          </span>
                          {dayTimes.overridden && (
                            <Clock className="h-3.5 w-3.5 text-amber-400" title="Custom time for this day" data-testid={`schedule-day-time-override-${job.id}`} />
                          )}
                          <ScheduleStatusBadge status={job.status} />
                          {isMultiDay && (
                            <Badge className="bg-purple-900 text-purple-300 text-xs">Multi-day</Badge>
                          )}
                        </div>
                        <h3 className="text-[var(--m-text)] font-medium truncate">{getCustomerName(job.customer_id)}</h3>
                        {job.site_id && getSiteName(job.site_id) && (
                          <p className="text-[var(--m-text-secondary)] text-sm flex items-center gap-1 mt-1">
                            <MapPin className="h-3 w-3 flex-shrink-0" />
                            <span className="truncate">{getSiteName(job.site_id)}</span>
                          </p>
                        )}
                        {job.equipment_type && (
                          <p className="text-[var(--m-text-muted)] text-sm flex items-center gap-1 mt-1">
                            <Truck className="h-3 w-3 flex-shrink-0" />
                            {job.equipment_type}
                          </p>
                        )}
                      </div>
                      <ChevronRight className="h-5 w-5 text-[var(--m-text-muted)] flex-shrink-0 mt-1" />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
      )}
    </MobileLayout>
  );
};

const ScheduleStatusBadge = ({ status }) => {
  const styles = {
    not_allocated: { bg: 'bg-red-900/50', text: 'text-red-300', label: 'Not Allocated' },
    partially_allocated: { bg: 'bg-orange-900/50', text: 'text-orange-300', label: 'Partial' },
    allocated: { bg: 'bg-blue-900/50', text: 'text-blue-300', label: 'Allocated' },
    sent: { bg: 'bg-amber-900/50', text: 'text-amber-300', label: 'Sent' },
    confirmed: { bg: 'bg-green-900/50', text: 'text-green-300', label: 'Confirmed' },
    mixed: { bg: 'bg-orange-900/50', text: 'text-orange-300', label: 'Mixed' },
    tbc: { bg: 'bg-[var(--m-bg-elevated)]/50', text: 'text-[var(--m-text-body)]', label: 'TBC' },
    docketed: { bg: 'bg-cyan-900/50', text: 'text-cyan-300', label: 'Docketed' },
    invoiced: { bg: 'bg-[var(--m-bg-elevated)]/50', text: 'text-[var(--m-text-body)]', label: 'Invoiced' },
  };
  const s = styles[status] || { bg: 'bg-[var(--m-bg-elevated)]/50', text: 'text-[var(--m-text-body)]', label: status };
  return <Badge className={`${s.bg} ${s.text} text-xs border-0`}>{s.label}</Badge>;
};

export default MobileSchedule;
